import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { InputField, TextareaField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-client';
import { useReapprovisionner } from '../api/stock.api';

/**
 * Réassort rapide. Une quantité négative est acceptée : elle enregistre un
 * retour fournisseur, et passe par le même service de stock (règle 6).
 */
export function RestockDialog({
  produit,
  onFermer,
}: {
  produit: { id: string; nom: string } | null;
  onFermer: () => void;
}) {
  const [quantite, setQuantite] = useState('1');
  const [note, setNote] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const reapprovisionner = useReapprovisionner();
  const toast = useToast();

  useEffect(() => {
    if (produit) {
      setQuantite('1');
      setNote('');
      setErreur(null);
    }
  }, [produit]);

  if (!produit) return null;

  const valeur = Number(quantite);
  const invalide = !Number.isInteger(valeur) || valeur === 0;

  async function valider() {
    if (!produit || invalide) {
      setErreur('Saisissez un nombre entier différent de zéro.');
      return;
    }
    try {
      await reapprovisionner.mutateAsync({
        id: produit.id,
        input: { quantite: valeur, note: note.trim() || null },
      });
      toast.succes(
        valeur > 0
          ? `${valeur} unité(s) ajoutée(s) à « ${produit.nom} ».`
          : `${-valeur} unité(s) retirée(s) de « ${produit.nom} ».`,
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : "Le réassort n'a pas pu être enregistré.");
    }
  }

  return (
    <Dialog
      ouvert
      onFermer={onFermer}
      titre="Réapprovisionner"
      description={produit.nom}
      taille="sm"
      pied={
        <>
          <Button variant="outline" onClick={onFermer}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void valider()} chargement={reapprovisionner.isPending}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <InputField
          label="Quantité"
          type="number"
          step={1}
          value={quantite}
          onChange={(e) => setQuantite(e.target.value)}
          aide="Nombre positif pour une entrée, négatif pour un retour."
          erreur={erreur ?? undefined}
          requis
        />
        <TextareaField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
        <Encart>
          Le mouvement est enregistré dans l'historique du produit et reste consultable.
        </Encart>
      </div>
    </Dialog>
  );
}
